import React, { useState } from 'react';
import { PDFDocument, rgb, StandardFonts, PDFFont, PDFPage } from 'pdf-lib';
// @ts-ignore
import * as fontkit from 'fontkit';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getDrStudentByGroup } from '../services/EP/EPApplyGroupService';

interface Student {
  StuNo: string;
  StuName: string;
}

interface CompletionCertificatePDFLibProps {
  schoolName: string;
  groupNo: string;
  groupName: string;
  students?: Student[];
  teacherName: string;
  topic?: string;
  onGenerate?: () => void;
}

const CompletionCertificatePDFLib: React.FC<CompletionCertificatePDFLibProps> = ({
  schoolName,
  groupNo,
  groupName,
  students,
  teacherName,
  topic,
  onGenerate
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const generatePDF = async () => {
    setIsLoading(true);
    console.log('使用 pdf-lib 生成PDF按鈕被點擊了！');
    console.log('組件參數:', { schoolName, groupNo, groupName, teacherName });
    
    try {
      // 先測試ZIP功能
      console.log('🧪 開始ZIP功能測試...');
      const testZip = new JSZip();
      testZip.file('test.txt', 'Hello World!');
      
      try {
        const testZipBlob = await testZip.generateAsync({ type: 'blob' });
        console.log('✅ ZIP功能測試成功，大小:', testZipBlob.size);
      } catch (testError) {
        console.error('❌ ZIP功能測試失敗:', testError);
        alert('ZIP功能不可用：' + (testError instanceof Error ? testError.message : String(testError)));
        return;
      }

      // 獲取學生資料
      let studentsData: Student[] = [];
      if (students && students.length > 0) {
        studentsData = students;
      } else {
        // 從API獲取學生資料
        try {
          const studentList = await getDrStudentByGroup(groupNo) as any[];
          studentsData = studentList.map((student: any) => ({
            StuNo: student.StuNo,
            StuName: student.StuName
          }));
        } catch (error) {
          console.error('獲取學生資料失敗:', error);
          studentsData = [{ StuNo: 'TEST001', StuName: '測試學生' }];
        }
      }

      console.log('學生資料:', studentsData);

      if (studentsData.length === 0) {
        alert('沒有找到學生資料，無法生成證書');
        return;
      }

      // 載入PDF模板
      const templatePath = '/imagedoc/114年度五階段完賽證明(定).pdf';
      let templateBytes: ArrayBuffer | null = null;

      console.log(`🔍 開始載入PDF模板: ${templatePath}`);
      try {
        const templateResponse = await fetch(templatePath);
        if (!templateResponse.ok) {
          throw new Error(`模板載入失敗 - HTTP狀態: ${templateResponse.status}`);
        }
        templateBytes = await templateResponse.arrayBuffer();
        console.log(`✅ 成功載入PDF模板，大小: ${templateBytes.byteLength} bytes`);
      } catch (error) {
        console.error('❌ 載入PDF模板時發生嚴重錯誤:', error);
        alert(`無法載入必要的PDF模板檔案，請確認檔案位於 public${templatePath}\n\n錯誤詳情: ${error}`);
        setIsLoading(false);
        return;
      }

      // 載入中文字體
      const fontPath = '/assets/fonts/TW-Kai-98_1.ttf';
      let fontBytes: ArrayBuffer | null = null;

      console.log(`🔍 開始載入中文字體: ${fontPath}`);
      try {
        const fontResponse = await fetch(fontPath);
        if (!fontResponse.ok) {
          throw new Error(`字體載入失敗 - HTTP狀態: ${fontResponse.status}`);
        }
        fontBytes = await fontResponse.arrayBuffer();
        console.log(`✅ 成功載入中文字體，大小: ${fontBytes.byteLength} bytes`);
      } catch (error) {
        console.warn('⚠️ 載入中文字體失敗，將使用預設字體:', error);
        // 這不是致命錯誤，可以繼續執行，但會顯示警告
      }

      const zip = new JSZip();
      console.log(`📦 開始為 ${studentsData.length} 位學生生成個別證書...`);

      // 使用 Promise.all 確保所有非同步操作完成
      const pdfPromises = studentsData.map(async (student, i) => {
        console.log(`🔄 [${i + 1}/${studentsData.length}] 開始生成 ${student.StuName} 的證書...`);
        try {
          let pdfDoc: PDFDocument;
          if (templateBytes) {
            pdfDoc = await PDFDocument.load(templateBytes);
          } else {
            pdfDoc = await PDFDocument.create();
          }

          pdfDoc.registerFontkit(fontkit);

          let font: PDFFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
          if (fontBytes) {
            try {
              font = await pdfDoc.embedFont(fontBytes);
            } catch (error) {
              console.warn(`[${student.StuName}] 字體嵌入失敗，使用預設字體:`, error);
            }
          }

          let page: PDFPage;
          if (templateBytes) {
            page = pdfDoc.getPage(0);
          } else {
            page = pdfDoc.addPage();
          }
          const { width, height } = page.getSize();

          // 學校名稱
          const schoolNameText = schoolName;
          const schoolNameWidth = font.widthOfTextAtSize(schoolNameText, 40);
          page.drawText(schoolNameText, {
            x: 90, // 水平置中
            y: 495,
            size: 30,
            font: font,
            color: rgb(0, 0, 0),
          });

          // 學生姓名
          page.drawText(student.StuName, {
            x: 160, // 根據版面微調的固定X值
            y: 450,
            size: 36,
            font: font,
            color: rgb(0, 0, 0),
          });

          const pdfBytes = await pdfDoc.save();
          const fileName = `完賽證明_${schoolName.trim()}_${student.StuName.trim()}.pdf`;
          console.log(`  [${student.StuName.trim()}] PDF 生成完成，大小: ${pdfBytes.length} bytes`);
          return { fileName, pdfBytes };

        } catch (studentError) {
          console.error(`❌ [${student.StuName}] 證書生成失敗:`, studentError);
          return null; // 返回 null 表示此學生處理失敗
        }
      });

      // 等待所有 PDF 生成完成
      const results = await Promise.all(pdfPromises);

      // 將成功生成的 PDF 添加到 ZIP
      let successCount = 0;
      results.forEach(result => {
        if (result) {
          zip.file(result.fileName, result.pdfBytes);
          successCount++;
        }
      });

      console.log(`✅ 共 ${successCount} 個 PDF 處理完成。`);

      if (successCount === 0) {
        alert('所有學生的證書都生成失敗，無法下載 ZIP 檔案。請檢查控制台以獲取詳細錯誤。');
        return;
      }
      
      console.log('🔄 開始生成 ZIP 檔案...');
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      console.log(`📦 ZIP 檔案生成完成，大小: ${zipBlob.size} bytes`);

      if (zipBlob.size === 0) {
        throw new Error('ZIP 檔案大小為0，生成失敗');
      }

      const zipFileName = `完賽證明_${schoolName.trim()}_${groupName.trim()}_全部學生.zip`;
      console.log(`📁 準備下載ZIP檔案: ${zipFileName}`);
      
      saveAs(zipBlob, zipFileName);
      console.log('✅ 下載指令已發出。');

      if (onGenerate) {
        onGenerate();
      }
      
      console.log('🎉 所有流程完成！');
      alert(`已成功為 ${successCount} 位學生生成證書並打包下載！`);
      
    } catch (error) {
      console.error('生成PDF時發生錯誤:', error);
      alert('生成PDF時發生錯誤，請稍後再試。錯誤：' + error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ margin: '10px 0' }}>
      <button 
        className="btn btn-info btn-sm"
        onClick={generatePDF}
        disabled={isLoading}
        title="生成完賽證明PDF"
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            生成中...
          </>
        ) : (
          <>
            <i className="fas fa-file-pdf me-1"></i>
            下載完賽證明書
          </>
        )}
      </button>
    </div>
  );
};

export default CompletionCertificatePDFLib; 